// Shell-level client state (0.7). Zustand store for UI state that:
//   - lives across route changes within an authenticated session,
//   - is not server data (TanStack Query owns that — see lib/queryClient.ts),
//   - is not engagement/scope-derived (ContextChain owns that),
//   - is not cross-tab-broadcast (BusProvider owns that).
//
// Starting scope = minimal. `commandPaletteOpen` is the live UI-state slice for the ⌘K
// Command Palette / "Go to…" — which HAS landed (was a deferred peripheral in the handoff):
// ShellCommandPalette.tsx (mounted unconditionally in AppShell.tsx) reads it, and TopNav.tsx's
// "Go to…" button + the global ⌘K / Ctrl+K hotkey drive it. Future slices (recent-engagements,
// density preference, etc.) extend this store rather than spawning parallel ones.
import type { ReactNode } from "react";
import { create } from "zustand";

export interface ShellState {
  commandPaletteOpen: boolean;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;
  // Ask AI right-overlay drawer. Mutually exclusive with the Context Inspector — when this is true the
  // right slot renders the Ask AI drawer instead of the Inspector (one right surface at a time).
  askAiOpen: boolean;
  openAskAi: () => void;
  closeAskAi: () => void;
  toggleAskAi: () => void;
  // Row 1.12 — app-bar contextual actions. AppShell.tsx's own deferral comment ("the deferred
  // toolbar") was a punt, not a lock (no cited spec row/authority) — this is a runtime
  // REGISTRATION channel, not a new manifest field (a manifest field would be its own
  // contract-impact CFR to CAT25's owner). A mounted app calls `setAppBarActions([...])` in an
  // effect to populate its own contextual buttons in TopNav, and `setAppBarActions([])` (or an
  // unmount cleanup) to clear them — no live producer calls this yet (CFR'd), but the channel
  // itself is real: TopNav's AppBarActions.tsx reads and renders it unconditionally.
  appBarActions: ReactNode[];
  setAppBarActions: (actions: ReactNode[]) => void;
  // Embedded-apps runtime (CFR-VW-145) — app focus/Max: hide the shell chrome (top header + sidebar)
  // for a near-full-screen app. A shell VIEW flag (per the redesign's "focus/max are shellStore view
  // flags"). In-memory only — refresh-survival would be a DB per-actor account_profile column (the
  // theme_preference precedent); deferred until wanted, so this never touches client storage.
  appMax: boolean;
  setAppMax: (v: boolean) => void;
  toggleAppMax: () => void;
  // NOTE: split view moved to the URL (`?split=<app>`) so it is deep-linkable + refresh-safe — see
  // OpenAppsStrip/SplitPane/AppShell (useSearch). No split slice lives here.
}

export const useShellStore = create<ShellState>((set) => ({
  commandPaletteOpen: false,
  openCommandPalette: () => set({ commandPaletteOpen: true }),
  closeCommandPalette: () => set({ commandPaletteOpen: false }),
  toggleCommandPalette: () =>
    set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
  askAiOpen: false,
  openAskAi: () => set({ askAiOpen: true }),
  closeAskAi: () => set({ askAiOpen: false }),
  toggleAskAi: () => set((s) => ({ askAiOpen: !s.askAiOpen })),
  appBarActions: [],
  setAppBarActions: (actions) => set({ appBarActions: actions }),
  appMax: false,
  setAppMax: (v) => set({ appMax: v }),
  toggleAppMax: () => set((s) => ({ appMax: !s.appMax })),
}));
